import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format } from 'date-fns';
import { RegistrationPlate, VehicleMakeLogo, StatusBadge } from '@/components/common';
import type { ScheduleItem } from '@/hooks';
import { useOpenWorkOrder, useViewedWorkOrderId } from '@/hooks/useWorkOrderWindow';
import { dateFnsLocale } from '@/utils/locale';

interface TodayScheduleWidgetProps {
  data?: ScheduleItem[];
}

export function TodayScheduleWidget({ data: schedule = [] }: TodayScheduleWidgetProps) {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const openWorkOrder = useOpenWorkOrder();
  const viewedWorkOrderId = useViewedWorkOrderId();

  const handleViewCalendar = () => {
    navigate('/calendar');
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('todaysSchedule')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {format(new Date(), 'EEEE d. MMMM', { locale: dateFnsLocale() })}
              </Typography>
            </Box>
            <Tooltip title={t('viewCalendar')}>
              <IconButton onClick={handleViewCalendar} size="small" aria-label={t('viewCalendar')} sx={{ minWidth: 44, minHeight: 44 }}>
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Divider />

        <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
          {schedule.map((item, index) => (
            <Box key={item.id}>
              <ListItem
                sx={{
                  py: 1.5,
                  cursor: 'pointer',
                  ...(item.id === viewedWorkOrderId && { backgroundColor: 'action.selected' }),
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
                aria-current={item.id === viewedWorkOrderId ? 'true' : undefined}
                onClick={(e) => openWorkOrder(item.id, e)}
              >
                <Box
                  sx={{
                    minWidth: 50,
                    mr: 2,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {item.time}
                  </Typography>
                </Box>
                <ListItemAvatar>
                  <VehicleMakeLogo make={item.vehicleMake} size={36} />
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RegistrationPlate registrationNumber={item.vehicleRegNumber} />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                        {item.service}
                      </Typography>
                      <StatusBadge status={item.status} size="small" />
                    </Box>
                  }
                />
              </ListItem>
              {index < schedule.length - 1 && <Divider component="li" />}
            </Box>
          ))}
        </List>
      </CardContent>
    </Card>
  );
}
